import React, { ChangeEvent, useState } from 'react';
import { Box, IconButton, IconButtonProps, TextField, useTheme } from '@material-ui/core';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import { isNaN, isNumber } from 'lodash/fp';
import styled from 'styled-components';
import { FieldProps } from 'formik';
import { validateMaxDecimals } from '../../utils';

export interface CounterProps {
    endAdornment?: JSX.Element;
    initialValue?: number;
    minLimit?: number;
    onChange?: FieldProps['field']['onChange'];
    maxDecimals?: number;
    name: string;
    isTextHighlighted?: boolean;
    isDanger?: boolean;
}

const CounterIconButton = styled((props: IconButtonProps & { isDanger?: boolean }) => (
    <IconButton {...props} />
))`
    && {
        color: ${props => props.isDanger ? props.theme.palette.error.main : props.theme.palette.success.main};
    }
`;

const Counter = React.forwardRef<HTMLDivElement, CounterProps>(
    ({ initialValue, minLimit, onChange, maxDecimals, endAdornment, name, isTextHighlighted, isDanger, ...other }, forwardRef) => {
        const theme = useTheme();
        const [value, setValue] = useState<string | number | undefined>(isNumber(initialValue) ? initialValue : undefined);

        const onAddClick = () => {
            const newValue = isNumber(value) ? value + 1 : 1;
            const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals });

            setValue(validatedValue);

            if (onChange) {
                onChange({ target: { name, value: validatedValue } });
            }
        };

        const onMinusClick = () => {
            const newValue = isNumber(value) ? value - 1 : 0;
            const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals });

            if (!isNumber(minLimit)) {
                setValue(validatedValue);
                if (onChange) {
                    onChange({ target: { name, value: validatedValue } });
                }
                return;
            }

            if (newValue <= minLimit) {
                setValue(minLimit);
                if (onChange) {
                    onChange({ target: { name, value: minLimit } });
                }
                return;
            }

            setValue(validatedValue);
            if (onChange) {
                onChange({ target: { name, value: validatedValue } });
            }
        };

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            const newValue = parseFloat(e.target.value);
            const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals });

            if (isNaN(validatedValue)) {
                setValue('');
                if (onChange) {
                    onChange({ target: { name, value: '' } });
                }
                return;
            }

            if (validatedValue === 0 && isNumber(validatedValue)) {
                setValue(validatedValue);
                if (onChange) {
                    onChange({ target: { name, value: validatedValue } });
                }
                return;
            }

            if (isNumber(minLimit) && validatedValue > minLimit) {
                setValue(validatedValue);

                if (onChange) {
                    onChange({ target: { name, value: validatedValue } });
                }
            }
        };


        return (
            <Box display="flex" alignItems='center'>
                <CounterIconButton size='small' onClick={onMinusClick} aria-label="minus" isDanger={isDanger}>
                    <RemoveIcon />
                </CounterIconButton>
                <Box maxWidth="120px" width="100%">
                    <TextField
                        {...other}
                        name={name}
                        ref={forwardRef}
                        size='small'
                        variant='outlined'
                        type="number"
                        value={value}
                        onChange={handleChange}
                        InputProps={{
                            inputProps: {
                                step: 'any',
                            },
                            endAdornment,
                            style: {
                                paddingRight: '4px',
                                color: isTextHighlighted ? theme.palette.success.main : theme.palette.text.primary,
                            },
                    }}
                    />
                </Box>
                <CounterIconButton size='small' onClick={onAddClick} aria-label="plus" isDanger={isDanger}>
                    <AddIcon />
                </CounterIconButton>
            </Box>
        );
    });

export default Counter;
